import { router, useForm, usePage } from '@inertiajs/react'
import { useState } from 'react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { WeeklyScheduleGrid, ALL_DAYS_ON } from '@/shared/ui/weekly-schedule-grid'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { cn } from '@/shared/lib/utils'
import { batchUnitsPath, departmentsPath, organizationPath, unitsPath } from '@/shared/lib/routes'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  ACCREDITATION_FIELDS,
  UNIT_BATCH_FOLLOW_PARENT_SCHEDULE_DEFAULT,
} from '@/entities/organization/units/constants'
import { useFollowParentDepartmentSchedulePreview } from '@/entities/organization/units/useFollowParentDepartmentSchedulePreview'
import {
  hasLicenseOrAccreditationValues,
  isBlankIdentifier,
  snakeCaseAccreditation,
} from '@/entities/organization/units/licenseIdentifiers'
import { Separator } from '@/shared/ui/separator'

interface DepartmentOption {
  value: number
  label: string
  siteName: string | null
}

interface Props {
  departments: DepartmentOption[]
  departmentId?: number | null
  customDaySchedule: WeekdaySchedule
  parentDaySchedule: WeekdaySchedule
  [key: string]: unknown
}

interface UnitRow {
  _key: number
  name: string
}

interface BatchFormData {
  sharedDepartmentId: number | null
  sharedLicenseNumber: string
  sharedFollowParentSchedule: boolean
  sharedCustomDaySchedule: WeekdaySchedule
  sharedAccreditation: Record<string, string>
  units: UnitRow[]
}

// Client-only validation keys that are not persisted form fields. Inertia types
// form.errors by the data keys, so these synthetic keys are set/read via a cast.
type BatchClientErrors = Partial<
  Record<'base' | 'licenseOrAccreditation' | 'sharedDepartmentId' | 'unitIndex', string>
>

let nextRowKey = 0
const makeUnitRow = (): UnitRow => ({ _key: nextRowKey++, name: '' })

const NewBatchPage = () => {
  const { departments, departmentId, customDaySchedule, parentDaySchedule } = usePage<Props>().props

  useBreadcrumbs([
    { label: t('nav.administration'), href: organizationPath() },
    { label: t('nav.departmentsAndUnits'), href: departmentsPath() },
    { label: t('nav.units'), href: unitsPath() },
    { label: t('organization.units.batchCreate.title') },
  ])

  const canSubmit = useCan(Resources.UNIT, Actions.CREATE)
  const initialDepartmentId = departmentId ?? null

  const form = useForm<BatchFormData>({
    sharedDepartmentId: initialDepartmentId,
    sharedLicenseNumber: '',
    sharedFollowParentSchedule: UNIT_BATCH_FOLLOW_PARENT_SCHEDULE_DEFAULT,
    sharedCustomDaySchedule: customDaySchedule ?? { ...ALL_DAYS_ON },
    sharedAccreditation: {},
    units: [makeUnitRow(), makeUnitRow()],
  })

  const [previewRetryNonce, setPreviewRetryNonce] = useState(0)
  const { effectiveParentPreview, parentPreviewPending, parentPreviewFailed } =
    useFollowParentDepartmentSchedulePreview({
      enabled: form.data.sharedFollowParentSchedule,
      departmentId: form.data.sharedDepartmentId,
      parentDaySchedule,
      initialDepartmentId,
      retryNonce: previewRetryNonce,
    })

  const addUnit = () => {
    form.setData('units', [...form.data.units, makeUnitRow()])
  }

  const removeUnit = (key: number) => {
    form.setData(
      'units',
      form.data.units.filter((row) => row._key !== key),
    )
  }

  const updateUnitName = (key: number, name: string) => {
    form.setData(
      'units',
      form.data.units.map((row) => (row._key === key ? { ...row, name } : row)),
    )
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!form.data.sharedDepartmentId) {
      form.clearErrors()
      form.setError('sharedDepartmentId', t('organization.units.form.departmentRequired'))
      document
        .getElementById('sharedDepartment')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (
      !hasLicenseOrAccreditationValues(form.data.sharedLicenseNumber, form.data.sharedAccreditation)
    ) {
      form.clearErrors()
      form.setError(
        'licenseOrAccreditation' as keyof BatchFormData,
        t('organization.units.form.licenseOrAccreditationRequired'),
      )
      document
        .getElementById('sharedLicenseNumber')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    const missingNameIndex = form.data.units.findIndex((row) => isBlankIdentifier(row.name))
    if (missingNameIndex >= 0) {
      const failingUnit = form.data.units[missingNameIndex]
      form.clearErrors()
      form.setError({
        base: t('organization.units.form.nameRequired'),
        unitIndex: String(missingNameIndex),
      } as BatchClientErrors as Partial<Record<keyof BatchFormData, string>>)
      document
        .getElementById(`unit_name_${failingUnit._key}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    form.transform((data) => ({
      shared: {
        department_id: data.sharedDepartmentId,
        license_number: data.sharedLicenseNumber.trim(),
        follow_parent_schedule: data.sharedFollowParentSchedule,
        custom_day_schedule: data.sharedCustomDaySchedule,
        accreditation: snakeCaseAccreditation(data.sharedAccreditation),
      },
      units: data.units.map(({ name }) => ({ name: name.trim() })),
    }))
    form.post(batchUnitsPath())
  }

  const genericErrors = form.errors as Record<string, string | undefined>
  const baseError = genericErrors.base
  const unitErrorIndex = genericErrors.unitIndex ? Number(genericErrors.unitIndex) : null

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold">{t('organization.units.batchCreate.title')}</h1>

      <form onSubmit={handleSubmit} className="space-y-8" noValidate>
        {/* Shared section */}
        <div className="space-y-4 rounded-lg border p-5">
          <h2 className="text-base font-semibold">
            {t('organization.units.batchCreate.sharedSection')}
          </h2>

          <div className="space-y-1.5">
            <Label htmlFor="sharedDepartment">{t('organization.units.form.department')} *</Label>
            <SearchableSelect
              id="sharedDepartment"
              value={form.data.sharedDepartmentId?.toString() ?? ''}
              onValueChange={(value) =>
                form.setData('sharedDepartmentId', value ? Number(value) : null)
              }
              placeholder={t('organization.units.form.selectDepartment')}
              aria-required
              options={departments.map((dept) => ({
                value: dept.value.toString(),
                label: dept.siteName ? `${dept.label} (${dept.siteName})` : dept.label,
              }))}
            />
            {form.errors.sharedDepartmentId && (
              <p className="text-sm text-destructive">{form.errors.sharedDepartmentId}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sharedLicenseNumber">
              {t('organization.units.form.licenseNumber')}
            </Label>
            <Input
              id="sharedLicenseNumber"
              value={form.data.sharedLicenseNumber}
              onChange={(event) => form.setData('sharedLicenseNumber', event.target.value)}
              placeholder={t('organization.units.form.licenseNumberPlaceholder')}
            />
            {genericErrors.licenseOrAccreditation && (
              <p className="text-sm text-destructive">{genericErrors.licenseOrAccreditation}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('schedule.loggingSchedule')}</Label>
            <WeeklyScheduleGrid
              value={form.data.sharedCustomDaySchedule}
              onChange={(next) => form.setData('sharedCustomDaySchedule', next)}
              followParent={form.data.sharedFollowParentSchedule}
              onFollowParentChange={(checked) =>
                form.setData('sharedFollowParentSchedule', checked)
              }
              parentPreview={effectiveParentPreview}
              parentPreviewPending={parentPreviewPending}
              parentPreviewFailed={parentPreviewFailed}
              onParentPreviewRetry={() => setPreviewRetryNonce((nonce) => nonce + 1)}
              parentLabel={t('schedule.followSiteSchedule')}
            />
            {form.errors.sharedCustomDaySchedule && (
              <p className="text-sm text-destructive">{form.errors.sharedCustomDaySchedule}</p>
            )}
          </div>

          <div className="space-y-3 pt-1">
            <p className="text-sm font-medium">
              {t('organization.units.form.accreditationSection')}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {ACCREDITATION_FIELDS.map(({ key, label }) => (
                <div key={key} className="space-y-1.5">
                  <Label htmlFor={`acc_${key}`}>{label}</Label>
                  <Input
                    id={`acc_${key}`}
                    value={form.data.sharedAccreditation[key] ?? ''}
                    onChange={(event) =>
                      form.setData('sharedAccreditation', {
                        ...form.data.sharedAccreditation,
                        [key]: event.target.value,
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Unit rows section */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold">
            {t('organization.units.batchCreate.unitRowsSection')}
          </h2>

          {form.data.units.map((row, index) => (
            <div key={row._key} className="flex items-center gap-3">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor={`unit_name_${row._key}`}>
                  {t('organization.units.batchCreate.unitLabel', { number: index + 1 })} *
                </Label>
                <Input
                  id={`unit_name_${row._key}`}
                  value={row.name}
                  onChange={(event) => updateUnitName(row._key, event.target.value)}
                  placeholder={t('organization.units.form.namePlaceholder')}
                  className={cn(index === unitErrorIndex && 'border-destructive')}
                  required
                />
                {index === unitErrorIndex && baseError && (
                  <p className="text-sm text-destructive">{baseError}</p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-6 shrink-0"
                disabled={form.data.units.length <= 1}
                onClick={() => removeUnit(row._key)}
              >
                {t('organization.units.batchCreate.removeUnit')}
              </Button>
            </div>
          ))}

          <Button type="button" variant="outline" onClick={addUnit}>
            {t('organization.units.batchCreate.addAnotherUnit')}
          </Button>
        </div>

        {unitErrorIndex === null && baseError && (
          <p className="text-sm text-destructive">{baseError}</p>
        )}

        <Separator />

        {!canSubmit && <NoAccessNotice />}

        <div className="flex gap-3">
          {canSubmit && (
            <Button type="submit" disabled={form.processing}>
              {form.processing
                ? t('organization.units.batchCreate.submitting')
                : t('organization.units.batchCreate.submit')}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => router.visit(unitsPath())}>
            {t('organization.units.form.cancel')}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default NewBatchPage
